import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Breadcrumbs, Button } from '@/shared/ui';

import { CoinBadge, type DiagnocoinFunding } from '@/entities/billing';
import { useStorageBlockedTooltip } from '@/entities/organization';

import { useMobileBreadcrumbs } from '@/widgets/Header';

import styles from './PatientRecordingsHeader.module.scss';

type PatientRecordingsHeaderProps = {
  total: number;
  funding: DiagnocoinFunding;
  canStartRecording: boolean;
  isStartRecordingDisabled: boolean;
  isStorageBlocked: boolean;
  isMobile?: boolean;
  className?: string;
  onStartRecording: () => void;
};

export const PatientRecordingsHeader: FC<PatientRecordingsHeaderProps> = ({
  total,
  funding,
  canStartRecording,
  isStartRecordingDisabled,
  isStorageBlocked,
  isMobile = false,
  className,
  onStartRecording,
}) => {
  const { formatMessage, formatNumber } = useIntl();
  const mobileBreadcrumbs = useMobileBreadcrumbs();
  const { wrapWithStorageTooltip } = useStorageBlockedTooltip({
    isStorageBlocked,
  });

  return (
    <header
      className={cn(styles.container, className)}
      data-component="patient-recordings-header"
    >
      {isMobile ? (
        <Breadcrumbs breadCrumbs={mobileBreadcrumbs} isClickable />
      ) : (
        <div className={styles.title}>
          <h3 className="h3">
            {formatMessage({
              id: 'patientRecordings.title',
              defaultMessage: 'Recordings',
            })}
          </h3>
          {total > 0 && (
            <span className={cn('h4', styles.count)}>
              {formatNumber(total)}
            </span>
          )}
        </div>
      )}

      {canStartRecording &&
        wrapWithStorageTooltip(
          <Button
            variant="secondary"
            size={isMobile ? 'medium' : undefined}
            className={styles.startButton}
            disabled={isStartRecordingDisabled}
            onClick={onStartRecording}
          >
            {funding.source === 'diagnocoins' && (
              <CoinBadge className={styles.coinBadge} />
            )}
            <FormattedMessage
              id="transcriptorRecording.startRecordingButton"
              defaultMessage="Start recording"
            />
          </Button>,
        )}
    </header>
  );
};
